import React, { useState, useEffect, useCallback, useMemo } from 'react';
import "../CreateOTSFleetEvent.scss";
import AddOTSComment from "../../OtsForm/AddOTSComment";
import { useAuthentication } from "meportal/AuthenticationProvider";
import ATACodeInput from './ATACodeInput';
import ProblemStatementInput from './ProblemStatementInput';
import AdvTimeInput from './ADVDateTimePicker';
import EventTypeDropdown from './EventTypeDropdown';
import FleetTypeDropdown from './FleetTypeDropdown';
import { OtsFleetDetailsData } from '../../../Models/CreateFleetOts';

interface OTSFleetEventDetailsProps {
    onFleetTypeChange: (fleet: string) => void;
    onOtsFleetDetailsChange: (isValid: boolean, data: OtsFleetDetailsData) => void;
    isDisabled?: boolean;
}

const OTSFleetEventDetails: React.FC<OTSFleetEventDetailsProps> = React.memo(({ onFleetTypeChange, onOtsFleetDetailsChange ,isDisabled}) => {
    const { userData } = useAuthentication();
    const [fleetType, setFleetType] = useState("");
    const [ataCode, setAtaCode] = useState("");
    const [isAtaCodeValid, setIsAtaCodeValid] = useState(false);
    const [advTime, setAdvTime] = useState<Date | null>(null);
    const [isAdvTimeValid, setIsAdvTimeValid] = useState(false);
    const [eventType, setEventType] = useState<string | null>(null);
    const [problemText, setProblemText] = useState("");
    const [isProblemTextValid, setIsProblemTextValid] = useState(false);
    const [message, setMessage] = useState("");
    const [isMessageValid, setIsMessageValid] = useState(false);
    const [ataCodeDescription, setATACodeDescription] = useState("");
    const [eventTypeCategory, setEventTypeCategory] = React.useState("");
    const [filesArray, setFilesArray] = React.useState<File[]>([]);
    const [hasPublicAttachment, setHasPublicAttachment] = React.useState(false);

    const validateForm = useCallback(() => {
        const isValid = 
            fleetType !== "" &&
            ataCode !== "" &&
            isAtaCodeValid &&
            advTime !== null &&
            isAdvTimeValid &&
            eventType !== null &&
            problemText !== "" &&
            isProblemTextValid &&
            isMessageValid;

        const formData: OtsFleetDetailsData = {
            fleetType,
            ataCode,
            ataCodeDescription,
            eventType,
            eventTypeCategory,
            advTime,
            problemStatement: problemText,
            message,
            filesArray,
            hasPublicAttachment
        };
        
        onOtsFleetDetailsChange(isValid, formData);
    }, [fleetType, ataCode, isAtaCodeValid, advTime, isAdvTimeValid, eventType, problemText, isProblemTextValid, ataCodeDescription, eventTypeCategory, onOtsFleetDetailsChange, message, filesArray, hasPublicAttachment, isMessageValid]);

    useEffect(() => {
        validateForm();
    }, [validateForm]);

    const handleFleetTypeChange = useCallback((selectedFleet: string) => {
        setFleetType(selectedFleet);
        onFleetTypeChange(selectedFleet);
    }, [onFleetTypeChange]);

    const handleATACodeChange = useCallback((code: string, description: string, isValid: boolean) => {
        setAtaCode(code);
        setATACodeDescription(description);
        setIsAtaCodeValid(isValid);
    }, []);
    
    const handleEventTypeChange = useCallback((eventType: string | null, category: string) => {
        setEventType(eventType);
        setEventTypeCategory(category);
    }, []);

    const handleAdvTimeChange = useCallback((time: Date | null, isValid: boolean) => {
        setAdvTime(time);
        setIsAdvTimeValid(isValid);
    }, []);

    const handleMessageDataChange = useCallback((data: any) => {
        setMessage(data.messageBoxText);
        setIsMessageValid(data.isValidComment);
    }, []);

    const postEventData = useCallback((data: any) => {
        console.log('post event data : ', data);
    }, []);

    const handleActiveHeader = useCallback((data: any) => {
        console.log('active header : ', data);
    }, []);


    const handleTwilioMessageSent = useCallback((twilioResponse: any) => {
        console.log('twilio response : ', twilioResponse);
    }, []);

    const handlePublicAttachmentCount = useCallback((count: number) => {
        const hasAttachments = count > 0;
        setHasPublicAttachment(hasAttachments);
    }, []);

    const handleFilesArrayChange = useCallback((fileArray: File[]) => {
        setFilesArray([...fileArray]);
    }, []);

    const memoizedAddOTSComment = useMemo(() => (
        <AddOTSComment
            handleMessageDataChange={handleMessageDataChange}
            postEventData={postEventData}
            handleActiveHeader={handleActiveHeader}
            handleTwilioMessageSent={handleTwilioMessageSent}
            handlePublicAttachmentCount={handlePublicAttachmentCount}
            userData={userData}
            handleFilesArrayChange={handleFilesArrayChange}
        />
    ), [handleMessageDataChange, postEventData, handleActiveHeader, handleTwilioMessageSent, handlePublicAttachmentCount, userData, handleFilesArrayChange]);

    return (
        <div>
            <div className="ots-fleet-event-details event-details-spacing mandatory-field">Event Details</div>
        <div className={isDisabled?'disabled-container':''}>
            <div className="dropdown-section">
                <div className="ots-fleet-dropdown-placeholder">
                    <FleetTypeDropdown onFleetTypeChange={handleFleetTypeChange} />
                </div>
                <div className="ots-fleet-dropdown-placeholder">
                    <ATACodeInput onATACodeChange={handleATACodeChange} />
                </div>
            </div>
            <div className="dropdown-section">
                <div className="ots-fleet-dropdown-placeholder event-type-select">
                    <EventTypeDropdown onEventTypeChange={handleEventTypeChange}/>
                </div>
            </div>
            <div className="dropdown-section">
                <div className="ots-fleet-dropdown-placeholder event-type-select">
                    <AdvTimeInput
                     onAdvTimeChange={handleAdvTimeChange} />
                </div>
            </div>
            <div className="ots-fleet-dropdown-placeholder">
            <ProblemStatementInput
                setProblemText={setProblemText}
                setIsProblemTextValid={setIsProblemTextValid}
                charLimit={80}
            />
      </div>
            <div className="ots-fleet-dropdown-placeholder comment-spacing">
                 {memoizedAddOTSComment}
            </div>
        </div>
        </div>
    );
});

export default OTSFleetEventDetails;